/**
 * @lodex-pro/web-admin — admin console navigation (Phase 25 / Task 25.1, DEC-111).
 *
 * One entry per left-nav section. Each routes to a placeholder until its real
 * surface ships in a later Phase 25 task. Icons are lucide-react (same set as
 * web-dispatch). Sign Out is rendered separately at the bottom of the sidebar.
 */
import * as React from 'react';
import {
  Activity,
  Layers,
  Building2,
  Network,
  MapPin,
  RefreshCw,
  Sliders,
  Settings,
  KeyRound,
  Mail,
  Receipt,
  Factory,
  Users,
} from 'lucide-react';

export interface AdminNavItem {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  readonly icon: React.ReactNode;
  /** Description shown on the placeholder page. */
  readonly description: string;
  /** Phase 25 task that will build the real surface. */
  readonly task: string;
}

const ICON = 18;

export const ADMIN_NAV: readonly AdminNavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: <Layers size={ICON} aria-hidden="true" />,
    description: 'Platform health overview — sync status, catalog counts, and tenant status.',
    task: 'Task 25.8',
  },
  // #56 PR 1b (DEC-362) — the ops console: Health + Errors across every tenant.
  {
    id: 'ops',
    label: 'Operations',
    path: '/ops',
    icon: <Activity size={ICON} aria-hidden="true" />,
    description: 'Problems across every tenant: health, queues, scheduled jobs and errors.',
    task: '#56',
  },
  {
    id: 'tenants',
    label: 'Tenants',
    path: '/tenants',
    icon: <Building2 size={ICON} aria-hidden="true" />,
    description:
      'List, create, and suspend hauler tenants; set abbreviations and view setup status.',
    task: 'Task 25.2',
  },
  {
    id: 'operators',
    label: 'Operators',
    path: '/operators',
    icon: <Network size={ICON} aria-hidden="true" />,
    description: 'Search platform operators, set display names, and merge duplicates.',
    task: 'Task 25.3',
  },
  // Phase 21 / Task 21.7 follow-up — disposal companies, their modules, and the sign-up queue.
  {
    id: 'disposal',
    label: 'Disposal',
    path: '/disposal',
    icon: <Factory size={ICON} aria-hidden="true" />,
    description:
      'Disposal companies: activate their portal, switch their paid modules, and review sign-ups.',
    task: 'Task 21.7',
  },
  {
    id: 'wells',
    label: 'Wells',
    path: '/wells',
    icon: <MapPin size={ICON} aria-hidden="true" />,
    description: 'Search the platform wells catalog, add unlisted wells, and deactivate wells.',
    task: 'Task 25.4',
  },
  {
    id: 'catalog-sync',
    label: 'Catalog Sync',
    path: '/catalog-sync',
    icon: <RefreshCw size={ICON} aria-hidden="true" />,
    description: 'Manage OCC sync config and intervals, trigger runs, and review the change queue.',
    task: 'Task 25.5',
  },
  {
    id: 'tank-catalog',
    label: 'Tank Catalog',
    path: '/tank-catalog',
    icon: <Sliders size={ICON} aria-hidden="true" />,
    description:
      'Manage the standard tank catalog — add, edit, and deactivate standard tank types.',
    task: 'Task 25.6',
  },
  {
    id: 'site-types',
    label: 'Site Types',
    path: '/site-types',
    icon: <Sliders size={ICON} aria-hidden="true" />,
    description:
      'Manage the platform site-type catalog — add, edit, and deactivate the types haulers can choose from.',
    task: '86bc7raat',
  },
  {
    id: 'abbreviations',
    label: 'Abbreviations',
    path: '/abbreviations',
    icon: <Settings size={ICON} aria-hidden="true" />,
    description: 'Set hauler and platform-operator abbreviations that drive the HaulId prefix.',
    task: 'Task 25.7',
  },
  // 86baefu3r — per-tenant Mapbox map-load counts. Reporting only; no enforcement.
  {
    id: 'map-usage',
    label: 'Mapbox usage',
    path: '/map-usage',
    icon: <MapPin size={ICON} aria-hidden="true" />,
    description: 'Map loads per hauler tenant for a billing month — reporting only.',
    task: '86baefu3r',
  },
  {
    id: 'support-email',
    label: 'Support email',
    path: '/support-email',
    icon: <Mail size={ICON} aria-hidden="true" />,
    description:
      'The support address users see, and where mail sent to it is forwarded. Changes apply at once, no deploy.',
    task: 'OPEN-109',
  },
  // Phase 21 / Task 21.7 — Paelode's own invoicing of its customers.
  {
    id: 'billing',
    label: 'Billing',
    path: '/billing',
    icon: <Receipt size={ICON} aria-hidden="true" />,
    description: 'Billing plans, invoices and payments for the customers Paelode bills.',
    task: 'Task 21.7',
  },
  // Paelode team page — invite, resend, suspend and reactivate Paelode admins.
  {
    id: 'team',
    label: 'Paelode team',
    path: '/team',
    icon: <Users size={ICON} aria-hidden="true" />,
    description:
      'The people who can sign in to this admin console: invite, suspend and reactivate.',
    task: 'Paelode team',
  },
  // Quick 260827-pwd — voluntary password change for the signed-in admin. NOT a
  // forgot-password affordance, which stays excluded here on purpose.
  {
    id: 'account',
    label: 'Account',
    path: '/account',
    icon: <KeyRound size={ICON} aria-hidden="true" />,
    description: 'Change the password you use to sign in.',
    task: 'Quick 260827-pwd',
  },
];
